import { ReactElement, useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

const tipsData = [
  {
    id: '1',
    title: 'sleepTipHeading',
    desc: 'sleepTipDesc_1',
  },
  {
    id: '2',
    title: 'sleepTipHeading',
    desc: 'sleepTipDesc_2',
  },
  {
    id: '3',
    title: 'sleepTipHeading',
    desc: 'sleepTipDesc_3',
  },
  {
    id: '4',
    title: 'sleepTipHeading',
    desc: 'sleepTipDesc_4',
  },
  {
    id: '5',
    title: 'sleepTipHeading',
    desc: 'sleepTipDesc_5',
  },
];

const TipsSection = (): ReactElement => {
  const { t: tTips } = useTranslation('', { keyPrefix: '$pages.$home.$tipsSection' });
  const [activeSlide, setActiveSlide] = useState(0);
  const [lastSlide, setLastSlide] = useState(0);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const tipItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tipContentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tipsContainerRef = useRef<HTMLDivElement>(null);
  const { contextSafe } = useGSAP({ scope: tipsContainerRef });

  // Initialize: hide prev button and set initial positions
  useGSAP(
    () => {
      gsap.set(prevButtonRef.current, { autoAlpha: 0 });
    },
    { scope: tipsContainerRef },
  );

  const goToNextSlide = contextSafe(() => {
    if (activeSlide >= tipsData.length - 1) return;

    const tl = gsap.timeline();
    const slideOutContent = tipContentRefs.current[activeSlide];
    const slideInContent = tipContentRefs.current[activeSlide + 1];
    const allItems = tipItemRefs.current;

    // Hide next button on reaching last slide
    if (activeSlide === tipsData.length - 2) {
      tl.to(nextButtonRef.current, { autoAlpha: 0, duration: 0.3, ease: 'none' }, 0);
    }

    tl.to(slideOutContent, { y: '+=40px', duration: 0.65, ease: 'power3.inOut' }, 0)
      .to(allItems, { y: '-=100%', duration: 1, ease: 'power3.inOut' }, 0)
      .fromTo(
        slideInContent,
        { y: '-=40px' },
        { y: 0, duration: 0.65, ease: 'power3.inOut' },
        '-=0.7',
      )
      .to(slideOutContent, { y: '0px', duration: 0.65, ease: 'power3.inOut' }, 0)
      .to(prevButtonRef.current, { autoAlpha: 1, duration: 0.1, ease: 'none' });

    setLastSlide(activeSlide);
    setActiveSlide(activeSlide + 1);
  });

  const goToPrevSlide = contextSafe(() => {
    if (activeSlide <= 0) return;

    const tl = gsap.timeline();
    const slideOutContent = tipContentRefs.current[activeSlide];
    const slideInContent = tipContentRefs.current[activeSlide - 1];
    const allItems = tipItemRefs.current;

    // Hide prev button on reaching first slide
    if (activeSlide === 1) {
      tl.to(prevButtonRef.current, { autoAlpha: 0, duration: 0.3, ease: 'none' }, 0);
    }

    tl.to(slideOutContent, { y: '-=40px', duration: 0.65, ease: 'power3.inOut' }, 0)
      .to(allItems, { y: '+=100%', duration: 1, ease: 'power3.inOut' }, 0)
      .fromTo(
        slideInContent,
        { y: '+=40px' },
        { y: 0, duration: 0.65, ease: 'power3.inOut' },
        '-=0.7',
      )
      .to(nextButtonRef.current, { autoAlpha: 1, duration: 0.1, ease: 'none' });

    setLastSlide(activeSlide);
    setActiveSlide(activeSlide - 1);
  });

  return (
    <section className="base-section tips-section hp-section" ref={tipsContainerRef}>
      <div className="tips-container">
        <div className="sleeping-icon">
          <p className="icon-wrap">
            <span className="icon-sleeping" />
          </p>
        </div>
        <div className="tips-wrap">
          <div className="row">
            {tipsData.map((item, index) => (
              <div
                key={item.id}
                ref={el => {
                  tipItemRefs.current[index] = el;
                }}
                className={classnames('tip', { active: index === activeSlide })}
              >
                <div className="tip-inner">
                  <p className="tip-number">
                    <span>
                      <strong>
                        {tTips('sleepTip_1')}
                        <br />
                        {tTips('sleepTip_2')}
                      </strong>
                      <em>{item.id}</em>
                    </span>
                  </p>
                  <div
                    className="tip-content"
                    ref={el => {
                      tipContentRefs.current[index] = el;
                    }}
                  >
                    <p className="tip-content-paragraph">
                      <span className="first">{tTips(item.title)}</span>
                      <span>{tTips(item.desc)}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="prevnext">
            <button
              type="button"
              className="prevnext-button"
              id="prev"
              ref={prevButtonRef}
              onClick={goToPrevSlide}
              aria-label="Previous tip"
            />
            <button
              type="button"
              className="prevnext-button"
              id="next"
              ref={nextButtonRef}
              onClick={goToNextSlide}
              aria-label="Next tip"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TipsSection;
